
































































import validate from "@/directives/validate";
import { Component, Prop, Vue } from "vue-property-decorator";
import i18n from "@/i18n";
import { Route } from "vue-router";
import { bus } from "@/bus";
// components
import HeaderActionButtons from "@/components/layouts/HeaderActionButtons.vue";
import PowerPlanEvents from "@/components/powerPlans/PowerPlanEvents.vue";
import PowerPlanSolutions from "@/components/powerPlans/PowerPlanSolutions.vue";
import UnsavedFormModal from "@/components/common/modals/UnsavedFormModal.vue";
// types
import { FormComponent, FormComponentModel } from "@/services/autoSettings/types";
import { PowerPlanDetails, PowerPlanEvent } from "@/services/powerPlans/types";
import { Solution } from "@/services/solutions/types";
import { powerPlansValidators } from "@/components/powerPlans/validators";

// service methods
import { createPowerPlan, getPowerPlan, updatePowerPlan } from "@/services/powerPlans/powerPlanService";
import { getSetting, setSetting } from "@/services/autoSettings/autoSettingsService";
import { validateForm } from "@/services/validation/validationService";

    @Component<PowerPlanForm>({
        beforeRouteLeave(to, from, next) {
            this.onBeforeRouteLeave(to, next);
        },
        components: {
            PowerPlanEvents,
            PowerPlanSolutions,
            HeaderActionButtons,
            UnsavedFormModal
        },
        directives: {
            validate
        }
    })
export default class PowerPlanForm extends Vue {
        @Prop() powerPlanId!: string;
        // local data
        validators = powerPlansValidators;
        componentsData = [] as FormComponentModel[];
        powerPlan = {
            id: "" as string,
            name: "" as string,
            solutions: [] as Solution[],
            events: [] as PowerPlanEvent[]
        } as PowerPlanDetails;

        // used for comparing if form have changes
        priorPowerPlanState = JSON.parse(JSON.stringify(this.powerPlan)) as PowerPlanDetails;

        nextRoute = "";
        saveClicked = false;
        routeParams = {};
        continue = false;
        localId = this.powerPlanId || "";
        // lifecycle hooks
        mounted() {
            this.fetchData();
            this.initComponents();
        }

        // computed
        get componentsAutoSettingKey(): string {
            let settingsKey = "powerPlanFormComponents";
            settingsKey += this.localId ? "-edit" : "-create";
            return settingsKey;
        }

        get components(): FormComponentModel[] {
            return this.componentsData
                .sort((a, b) => a.order - b.order)
                .filter((name) => name.componentName !== "PowerPlanEvents" || this.localId);
        }

        // methods
        fetchData() {
            if (!this.localId) return;
            getPowerPlan(this.localId).then(response => {
                this.powerPlan = response;
                bus.$emit("documentTitle", this.powerPlan.name);
                this.priorPowerPlanState = JSON.parse(JSON.stringify(response));
            });
        }

        async initComponents() {
            let components = getSetting(this.componentsAutoSettingKey);
            if (!components) {
                await setSetting(this.componentsAutoSettingKey,
                    [
                        {
                            componentName: "PowerPlanEvents",
                            sectionName: i18n.t("powerPlanEvent.events"),
                            order: 0,
                            collapseState: true
                        },
                        {
                            componentName: "PowerPlanSolutions",
                            sectionName: i18n.t("solution.title"),
                            order: 1,
                            collapseState: true
                        }] as FormComponent[]);
                components = getSetting(this.componentsAutoSettingKey);
            }

            this.componentsData = components.map((x: FormComponent) => ({
                componentName: x.componentName,
                order: x.order,
                active: false,
                activeDropTarget: false,
                collapseState: x.collapseState,
                sectionName: x.sectionName
            }));
        }

        onComponentStateChanged(componentState: any) {
            const component = this.componentsData.find(x => x.componentName === componentState.name);
            if (!component) return;

            component.collapseState = componentState.state;
            setSetting(this.componentsAutoSettingKey,
                this.componentsData.map(x => (
                    {
                        componentName: x.componentName,
                        order: x.order,
                        collapseState: x.collapseState,
                        sectionName: x.sectionName
                    } as FormComponent)));
        }

        onBeforeRouteLeave(to: Route, next: any) {
            if (this.saveClicked || this.continue) {
                next();
            } else {
                this.nextRoute = to.name ? to.name : "";
                this.routeParams = to.params;
                this.childComponentsHandler();
                const noChanges = this.lodash.isEqual(this.priorPowerPlanState, this.powerPlan);
                if (noChanges) {
                    next();
                } else {
                    next(false);
                    (this.$refs.UnsavedFormModal as any).open();
                }
            }
        }

        compareObjects(firstObject: any, secondObject: any) {
            if (firstObject === secondObject) return true;

            if (!(firstObject instanceof Object) || !(secondObject instanceof Object)) return false;

            if (firstObject.constructor !== secondObject.constructor) return false;

            for (const property in firstObject) {
                // eslint-disable-next-line no-prototype-builtins
                if (!firstObject.hasOwnProperty(property)) continue;

                // eslint-disable-next-line no-prototype-builtins
                if (!secondObject.hasOwnProperty(property)) return false;

                if (firstObject[property] === secondObject[property]) continue;

                if (typeof (firstObject[property]) !== "object") return false;

                if (!this.compareObjects(firstObject[property], secondObject[property])) return false;
            }

            for (const property in secondObject) {
                // eslint-disable-next-line no-prototype-builtins
                if (secondObject.hasOwnProperty(property) && !firstObject.hasOwnProperty(property)) { return false; }
            }

            return true;
        }

        onContinue(data: boolean) {
            this.continue = data;
            if (data) this.$router.push({ name: this.nextRoute, params: this.routeParams });
            this.nextRoute = "";
        }

        async save(back: boolean) {
            if (this.saveClicked) return;

            this.childComponentsHandler();

            if (!await validateForm([this.validators])) {
                this.saveClicked = false;
                return;
            }

            if (!this.localId) {
                createPowerPlan(this.powerPlan as PowerPlanDetails).then((id: any) => {
                    if (back) this.$router.push("/power-plans");
                    else {
                        this.localId = id;
                        this.powerPlan.id = id;
                        this.priorPowerPlanState.id = id;
                        this.priorPowerPlanState.name = this.powerPlan.name;
                        this.saveClicked = false;
                    }
                });
            } else {
                updatePowerPlan(this.powerPlan).then(() => {
                    if (back) this.$router.push("/power-plans");
                    this.priorPowerPlanState.name = this.powerPlan.name;
                    this.saveClicked = false;
                });
            }
            this.priorPowerPlanState.solutions = this.powerPlan.solutions;
            this.saveClicked = true;
        }

        back() {
            window.history.go(-1);
        }

        childComponentsHandler() {
            const components = this.$refs.childComponent as any[];
            components.forEach(component => {
                if (typeof component.emitData === "function") { component.emitData(); }
            });
        }

        // event handlers
        onSolutionsUpdated(solutions: Solution[]) {
            this.powerPlan.solutions = solutions;
        }

        // drag & drop event handlers
        drag(event: any) {
            const component = this.componentsData.find(x => x.componentName === event.target.id);
            if (!component) { return; }

            event.dataTransfer.setDragImage((this.$refs[component.componentName] as any)[0], 1000, 0);
            event.dataTransfer.setData(
                "id",
                event.target.id
            );

            component.active = true;
        }

        dragLeave(event: any) {
            const component = this.componentsData.find(x => x.componentName === event.currentTarget.id);
            if (!component) return;
            component.activeDropTarget = false;
        }

        dragOver(event: any) {
            event.preventDefault();
            const component = this.componentsData.find(x => x.componentName === event.currentTarget.id);
            if (!component) return;
            if (component.active) return;
            component.activeDropTarget = true;
        }

        dragEnd() {
            this.componentsData.forEach(x => {
                x.activeDropTarget = false;
                x.active = false;
            });
        }

        swapComponents(event: any) {
            event.preventDefault();
            if (!event.currentTarget) {
                return;
            }

            const target = this
                .componentsData
                .find(x => event.currentTarget && x.componentName === event.currentTarget.id);
            const source = this
                .componentsData
                .find(x => event.dataTransfer && x.componentName === event.dataTransfer.getData("id"));

            if (!(source && target)) { return; }

            const sourceOrder = source.order;
            source.order = target.order;
            target.order = sourceOrder;

            setSetting(this.componentsAutoSettingKey,
                this.componentsData.map(x => ({
                    componentName: x.componentName,
                    order: x.order,
                    collapseState: x.collapseState,
                    sectionName: x.sectionName
                } as FormComponent)));
        }
}
